import React, { useEffect, useState, useContext } from 'react'
import {
    Card,
    CardBody,
    CardTitle,
    Row,
    Col,
    Input,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Button
} from 'reactstrap'

import Moment from 'react-moment'
import { Link, withRouter } from 'react-router-dom'
import CurrencyFormat from 'react-currency-format'

import ApiService from '../../../data/services/ApiService'
import CancelacionCFDIModal from '../../../components/CancelacionCFDIModal'
import ProgramarFacturaModal from './ProgramarFacturaModal'
import RegistrarPagoModal from './RegistrarPagoModal'
import { GeneralContext } from '../../../context/'
import { duplicateInvoiceAndNavigate } from '../../../helpers/duplicarFactura'
import { getProgramacionInicial } from '../../../helpers/facturasProgramadas'
import { calcularCobradoYCerrado } from '../../../helpers/facturaHelpers'

import toastr from 'toastr'
import 'toastr/build/toastr.min.css'

const hasActiveManualPayment = invoice => [
    ...(invoice?.pagosManualesPue || []),
    ...(invoice?.pagosManualesExternos || [])
].some(payment => payment.status === 'activo')

const Table = (props) => {
    const generalContext = useContext(GeneralContext)
    const { setLoading } = generalContext

    const [year, setYear] = useState()
    const [invoices, setInvoices] = useState()
    const [filteredData, setFilteredData] = useState([])
    const [cancelInvoiceModal, setCancelInvoiceModal] = useState(false)
    const [invoiceToCancel, setInvoiceToCancel] = useState()
    const [cancelReason, setCancelReason] = useState()
    const [folioSustitucion, setFolioSustitucion] = useState('')
    const [showFolioSustitucion, toggleFolioSustitucion] = useState(false)
    const [upcoming48h, setUpcoming48h] = useState([])
    const [invoiceToSchedule, setInvoiceToSchedule] = useState(null)
    const [invoiceForPayment, setInvoiceForPayment] = useState(null)

    useEffect(() => {
        setYear(props.year)
        // eslint-disable-next-line
    }, [props])

    useEffect(() => {
        (async () => {
            await _getInvoices()
        })()

        // eslint-disable-next-line
    }, [year]) // eslint-disable-line

    const _getInvoices = async () => {
        setLoading(true)
        try {
            const y = year || new Date().getFullYear()
            const fromDate = `${y.toString()}-01-01`
            const toDate = `${y.toString()}-12-31`
            const result = await ApiService.getFacturas(fromDate, toDate)

            setInvoices(result.data.data)
            setFilteredData(result.data.data)
            setUpcoming48h(result.data.upcoming48h || [])
        } catch (error) {
            if (error.response?.status === 429) {
                toastr.error('Demasiadas solicitudes. Intenta de nuevo en unos minutos.')
            } else {
                toastr.error(error.response?.data?.message || 'No pude cargar las facturas.')
            }
        } finally {
            setLoading(false)
        }
    }

    const _filterInvoices = async (e) => {
        setLoading(true)
        const keystroke = e.target.value.toLowerCase()
        const data = invoices.filter((item) => {
            const uuid = item.uuid || ''
            const folio = item.folio || ''
            const cliente = item.clientes[0].razonSocial || ''
            const status = item.status || ''

            return uuid.toLowerCase().includes(keystroke) ||
                cliente.toLowerCase().includes(keystroke) ||
                folio.toLowerCase().includes(keystroke) ||
                status.toLowerCase().includes(keystroke)
        })

        setFilteredData(data)
        setLoading(false)
    }

    const _cancelInvoice = async () => {
        setLoading(true)
        try {
            const response = await ApiService.cancelarFactura({
                uuid: invoiceToCancel,
                motivo: cancelReason.value,
                folioSustitucion
            })

            if (!response.data.success) {
                if (response.data.code === 'CFDI_STILL_ACTIVE') {
                    toastr.warning(response.data.message)
                } else {
                    toastr.error(response.data.message)
                }

                await _getInvoices()
                setLoading(false)

                return
            }

            toastr.success(response.data.message)
            setCancelInvoiceModal(false)
            await _getInvoices()
            setLoading(false)
            window.analytics.track('Factura cancelada', {
                uuid: invoiceToCancel,
                motivo: cancelReason,
                folioSustitucion
            })
        } catch (error) {
            toastr.error(error.response?.data?.message || 'No pude cancelar la factura.')
            setLoading(false)
        }
    }

    const deleteInvoiceDraft = async (id) => {
        try {
            await ApiService.deleteInvoiceDraft(id)
            await _getInvoices()

            toastr.success(`Has eliminado este borrador.`)
        } catch (error) {
            toastr.error(`No pude eliminar el borrador.`)
        }
    }

    const _duplicateInvoice = async (id) => {
        await duplicateInvoiceAndNavigate({
            invoiceId: id,
            history: props.history,
            setLoading
        })
    }

    const _verificarFacturaSat = async (id) => {
        try {
            setLoading(true)
            const response = await ApiService.verificarFacturaSat(id)

            if (response.data.code === 'CFDI_STILL_ACTIVE') {
                toastr.warning(response.data.message)
            } else {
                toastr.success(response.data.message)
            }

            await _getInvoices()
        } catch (error) {
            toastr.error(error.response?.data?.message || 'No pude actualizar el estado de la factura en el SAT.')
        } finally {
            setLoading(false)
        }
    }

    const _cerrarSaldoPendiente = async (id) => {
        try {
            setLoading(true)
            const response = await ApiService.cerrarSaldoPrefill(id)
            props.history.push('/facturas/nueva', {
                cerrarSaldoPrefill: response.data.data.prefill
            })
        } catch (error) {
            const message = error.response?.data?.message || 'No pude obtener los datos para cerrar el saldo.'
            toastr.error(message)
        } finally {
            setLoading(false)
        }
    }

    const __renderCancelarFacturaModal = () => {
        return (
            <CancelacionCFDIModal
                title={`Cancelar Factura ${invoiceToCancel}`}
                isOpen={cancelInvoiceModal}
                cancelReason={cancelReason}
                folioSustitucion={folioSustitucion}
                showFolioSustitucion={showFolioSustitucion}
                onCancelReasonChange={(value) => {
                    setCancelReason(value)

                    if (value?.value === '01') {
                        toggleFolioSustitucion(true)
                        return
                    }

                    toggleFolioSustitucion(false)
                    setFolioSustitucion('')
                }}
                onFolioSustitucionChange={setFolioSustitucion}
                onConfirm={_cancelInvoice}
                onRequestClose={() => setCancelInvoiceModal(false)}
            />
        )
    }

    const _donwloadInvoice = async (uuid, type) => {
        try {
            await ApiService.downloadInvoice(uuid, type)
        } catch (err) {
            if (err.response && err.response.data && err.response.data.code === 'NO_ORDERS_FOUND') {
                toastr.warning(err.response.data.message || 'No se encontraron pedidos para los filtros seleccionados')
            } else {
                toastr.error('Error al descargar factura')
            }

            console.log(err)
        }
    }

    const _downloadAcuseCancelacion = async (uuid, type) => {
        try {
            await ApiService.downloadAcuseCancelacion(uuid, type)
        } catch (err) {
            toastr.error('Error al descargar acuse de cancelación')
            console.log(err)
        }
    }

    const _openScheduleModal = (invoice) => {
        setInvoiceToSchedule(invoice)
    }

    const _closeScheduleModal = () => {
        setInvoiceToSchedule(null)
    }

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <Row className="mb-2">
                        <Col sm="4">
                            <div className="search-box mr-2 mb-2 d-inline-block">
                                <div className="position-relative">
                                    <Input type="text" className="form-control"
                                        placeholder="Buscar ..."
                                        onKeyUp={(e) => _filterInvoices(e)}
                                    />
                                    <i className="bx bx-search-alt search-icon"></i>
                                </div>
                            </div>
                        </Col>
                        <Col sm="8">
                            <div className="text-sm-right">
                                <Link to={`/facturas/programadas`} className="btn btn-outline-primary waves-effect waves-light mb-2 mr-2">
                                    <i className="mdi mdi-calendar-clock mr-1"></i> Programadas
                                </Link>
                                <Link to={`/facturas/nueva`} className="btn btn-success waves-effect waves-light mb-2 mr-2">
                                    <i className="mdi mdi-plus mr-1"></i> Nueva Factura
                                </Link>
                            </div>
                        </Col>
                    </Row>

                    {upcoming48h.length > 0 && (
                        <Card style={{ border: '1px solid #ffe08a', backgroundColor: '#fffdf4' }}>
                            <CardBody>
                                <h5 style={{ marginBottom: 16 }}>Próximas 48 horas</h5>
                                {upcoming48h.map((invoice) => (
                                    <Row key={invoice._id} className="align-items-center mb-3">
                                        <Col sm={3} xs={12}>
                                            <strong>{invoice.clientes?.[0]?.razonSocial || 'Sin receptor'}</strong>
                                        </Col>
                                        <Col sm={2} xs={12}>
                                            <small>{invoice.status}</small>
                                        </Col>
                                        <Col sm={2} xs={12}>
                                            <small>
                                                <Moment format="DD/MM/YYYY HH:mm">
                                                    {invoice.schedule?.scheduledFor}
                                                </Moment>
                                            </small>
                                        </Col>
                                        <Col sm={2} xs={12}>
                                            <small>{invoice.schedule?.isRecurring ? 'Recurrente' : 'Única'}</small>
                                        </Col>
                                        <Col sm={3} xs={12} style={{ textAlign: 'right' }}>
                                            <UncontrolledDropdown>
                                                <DropdownToggle href="#" className="card-drop" tag="i">
                                                    <i className="mdi mdi-dots-horizontal font-size-18"></i>
                                                </DropdownToggle>
                                                <DropdownMenu right>
                                                    {(invoice.status === 'programada' || invoice.status === 'fallida') && (
                                                        <>
                                                            <DropdownItem href={`/facturas/nueva/${invoice._id}`}>
                                                                <i className="mdi mdi-pencil font-size-16 text-primary mr-2"></i> Editar Factura
                                                            </DropdownItem>
                                                            <div className="dropdown-divider"></div>
                                                            <DropdownItem
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault()
                                                                    _openScheduleModal(invoice)
                                                                }}
                                                            >
                                                                <i className="mdi mdi-calendar-clock font-size-16 text-warning mr-2"></i> Gestionar Programación
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                </DropdownMenu>
                                            </UncontrolledDropdown>
                                        </Col>
                                    </Row>
                                ))}
                            </CardBody>
                        </Card>
                    )}

                    {filteredData.map((invoice) => (
                        <Card key={invoice._id} className={`tipo-comprobante-${invoice.tipoComprobante} invoice-status-${invoice.status} invoice-pagada-${invoice.pagada === true ? 'pagada' : 'porcobrar'}`}>
                            <div className={`left-bar ${invoice.tipoComprobante === 'E' ? 'bar-orange' : 'bar-blue'}`} />

                            <CardTitle style={{ fontSize: '130%' }}>
                                #{invoice.folio ? invoice.folio : '000'} {invoice.clientes[0].razonSocial}
                                {invoice.tipoComprobante === 'E' && <span className="badge nota-credito-badge">Nota de Crédito</span>}
                                {invoice.importadoDelSAT === true && (
                                    <span
                                        className="badge"
                                        title="Importada del Portal SAT"
                                        style={{
                                            marginLeft: 8,
                                            backgroundColor: '#e9ecef',
                                            color: '#495057',
                                            fontSize: '60%',
                                            verticalAlign: 'middle'
                                        }}
                                    >
                                        SAT
                                    </span>
                                )}
                            </CardTitle>
                            <CardBody>
                                <Row>
                                    <Col sm={5} xs={12}>
                                        <strong>Fecha de Timbrado</strong><br />
                                        <span><small><Moment format="DD/MM/YYYY">{invoice.fechaTimbrado}</Moment></small></span> <br /><br />
                                        <strong>Estado del CFDi:</strong> <span>{invoice.status.toLowerCase().replace(/^\w/, c => c.toUpperCase())}</span><br />
                                        {/* <strong>Tipo de Comprobante:</strong> <span>{getTipoComprobanteLabel(invoice.tipoComprobante)}</span><br /> */}
                                        <strong>UUID:</strong>
                                        <small>
                                            {invoice.status === 'borrador' && (
                                                <>00000000-0000-0000-0000-000000000000</>
                                            )}

                                            {(invoice.status === 'timbrada' || invoice.status === 'cerrada') && (
                                                <>{invoice.uuid.toUpperCase()}</>
                                            )}
                                        </small>
                                    </Col>
                                    <Col sm={5} xs={12}>
                                        <Row>
                                            <Col sm={6} xs={12} style={{
                                                padding: '16px 0',
                                                textAlign: 'left'
                                            }}>
                                                {invoice.status === 'borrador' && (
                                                    <span className="invoice-borrador">
                                                        Borrador
                                                    </span>
                                                )}

                                                {invoice.status === 'cancelada' && (
                                                    <span className="invoice-cancelada">
                                                        Cancelada
                                                    </span>
                                                )}

                                                {invoice.status === 'programada' && (
                                                    <span style={{
                                                        backgroundColor: '#fff3cd',
                                                        color: '#856404',
                                                        border: '1px solid #ffc107',
                                                        borderRadius: 4,
                                                        padding: '4px 8px',
                                                        fontSize: '75%',
                                                        fontWeight: 600
                                                    }}>
                                                        Programada
                                                    </span>
                                                )}

                                                {invoice.status === 'fallida' && (
                                                    <span style={{
                                                        backgroundColor: '#f8d7da',
                                                        color: '#721c24',
                                                        border: '1px solid #dc3545',
                                                        borderRadius: 4,
                                                        padding: '4px 8px',
                                                        fontSize: '75%',
                                                        fontWeight: 600
                                                    }}>
                                                        Fallida
                                                    </span>
                                                )}

                                                {invoice.status === 'timbrada' && (
                                                    <span className={invoice.pagada === true ? 'invoice-pagada' : 'invoice-porcobrar'}>
                                                        {invoice.pagada === true ? 'Pagada' : 'Pendiente de Pago'}
                                                    </span>
                                                )}

                                                {invoice.status === 'cerrada' && (
                                                    <span style={{
                                                        backgroundColor: 'var(--color-background-secondary, #f8f9fa)',
                                                        color: 'var(--color-text-secondary, #6c757d)',
                                                        border: '1px solid var(--color-border-tertiary, #dee2e6)',
                                                        borderRadius: 4,
                                                        padding: '4px 8px',
                                                        fontSize: '75%',
                                                        fontWeight: 600
                                                    }}>
                                                        Cerrada
                                                    </span>
                                                )}
                                            </Col>
                                            <Col sm={6} xs={12} style={{
                                                padding: '16px 0',
                                                textAlign: 'right'
                                            }}>
                                                <strong style={{
                                                    fontSize: '180%'
                                                }}>
                                                    <CurrencyFormat
                                                        value={invoice.total}
                                                        displayType="text"
                                                        thousandSeparator
                                                        prefix="$"
                                                        decimalScale={2}
                                                    />
                                                </strong>

                                                {invoice.cerradaConNotaCredito === true && (() => {
                                                    const { cobrado, cerrado } = calcularCobradoYCerrado(invoice)
                                                    return (
                                                        <div style={{ marginTop: 6, lineHeight: 1.6 }}>
                                                            <div style={{ fontSize: 12 }}>
                                                                <span style={{ color: 'var(--color-text-secondary, #6c757d)' }}>Cobrado:&nbsp;</span>
                                                                <span style={{ color: 'var(--color-text-success, #28a745)', fontWeight: 600 }}>
                                                                    <CurrencyFormat
                                                                        value={cobrado}
                                                                        displayType="text"
                                                                        thousandSeparator
                                                                        prefix="$"
                                                                        decimalScale={2}
                                                                    />
                                                                </span>
                                                            </div>
                                                            <div style={{ fontSize: 12 }}>
                                                                <span style={{ color: 'var(--color-text-secondary, #6c757d)' }}>Cerrado c/NC:&nbsp;</span>
                                                                <span style={{ color: 'var(--color-text-secondary, #6c757d)', fontWeight: 600 }}>
                                                                    <CurrencyFormat
                                                                        value={cerrado}
                                                                        displayType="text"
                                                                        thousandSeparator
                                                                        prefix="$"
                                                                        decimalScale={2}
                                                                    />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )
                                                })()}

                                                {(() => {
                                                    if (invoice.metodoPago !== 'PPD') return null
                                                    if (invoice.pagada === true) return null

                                                    const saldo = invoice.saldoPendiente != null
                                                        ? parseFloat(invoice.saldoPendiente)
                                                        : parseFloat(invoice.total) - parseFloat(invoice.montoPagado || 0)

                                                    if (saldo <= 0 || saldo >= parseFloat(invoice.total)) return null

                                                    return (
                                                        <div style={{ marginTop: 6 }}>
                                                            <span style={{
                                                                backgroundColor: '#fff3cd',
                                                                color: '#856404',
                                                                border: '1px solid #ffc107',
                                                                borderRadius: 4,
                                                                padding: '2px 8px',
                                                                fontSize: '75%',
                                                                fontWeight: 600,
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                Saldo pendiente:&nbsp;
                                                                <CurrencyFormat
                                                                    value={saldo}
                                                                    displayType="text"
                                                                    thousandSeparator
                                                                    prefix="$"
                                                                    decimalScale={2}
                                                                />
                                                            </span>
                                                        </div>
                                                    )
                                                })()}
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col sm={2} xs={12}>
                                        <UncontrolledDropdown>
                                            <DropdownToggle href="#" className="card-drop" tag="i">
                                                <i className="mdi mdi-dots-horizontal font-size-18"></i>
                                            </DropdownToggle>
                                            <DropdownMenu right>
                                                <DropdownItem href={`/facturas/${invoice._id}`}>
                                                    <i className="mdi mdi-note font-size-16 text-primary mr-2"></i> Ver factura
                                                </DropdownItem>
                                                <div className="dropdown-divider"></div>
                                                {invoice.importadoDelSAT !== true && (
                                                    <DropdownItem href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            _duplicateInvoice(invoice._id)
                                                        }}>
                                                        <i className="mdi mdi-content-copy font-size-16 text-primary mr-2"></i> Duplicar
                                                    </DropdownItem>
                                                )}

                                                {invoice.status === 'borrador' && (
                                                    <>
                                                        <div className="dropdown-divider"></div>
                                                        <DropdownItem href={`/facturas/nueva/${invoice._id}`}>
                                                            <i className="bx bxs-file-pdf font-size-16 text-success mr-2"></i> Editar Borrador
                                                        </DropdownItem>
                                                        <DropdownItem href="#"
                                                            onClick={() => {
                                                                deleteInvoiceDraft(invoice._id)
                                                            }}>
                                                            <i className="mdi mdi-cancel font-size-16 text-danger mr-2"></i> Eliminar Borrador
                                                        </DropdownItem>
                                                    </>
                                                )}

                                                {(['timbrada', 'cerrada', 'cancelación rechazada'].includes(invoice.status)) && (
                                                    <>
                                                        <DropdownItem href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                _donwloadInvoice(invoice.uuid, 'pdf')
                                                            }}>
                                                            <i className="bx bxs-file-pdf font-size-16 text-success mr-2"></i> Descargar PDF
                                                        </DropdownItem>

                                                        <DropdownItem href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                _donwloadInvoice(invoice.uuid, 'xml')
                                                            }}>
                                                            <i className="bx bxs-file-blank font-size-16 text-success mr-2"></i> Descargar XML
                                                        </DropdownItem>

                                                        <DropdownItem target="_blank" href={`https://api.whatsapp.com/send?text=¡Hola!+Te+envío+la+factura+PDF:+${ApiService.getBaseURL()}/api/facturas/get-pdf/${invoice.uuid}+y+XML:+${ApiService.getBaseURL()}/api/facturas/get-xml/${invoice.uuid}`}>
                                                            <i className="bx bxl-whatsapp font-size-16 text-success mr-2"></i> Compartir por WhatsApp
                                                        </DropdownItem>
                                                        <div className="dropdown-divider"></div>
                                                        {invoice.tipoComprobante === 'I' &&
                                                            invoice.status !== 'cerrada' &&
                                                            invoice.cerradaConNotaCredito !== true &&
                                                            ['PUE', 'PPD'].includes(invoice.metodoPago) &&
                                                            (invoice.pagada !== true || hasActiveManualPayment(invoice)) && (
                                                            <DropdownItem href="#" onClick={(e) => {
                                                                e.preventDefault()
                                                                setInvoiceForPayment(invoice)
                                                            }}>
                                                                <i className="mdi mdi-cash-check font-size-16 text-success mr-2"></i>
                                                                {hasActiveManualPayment(invoice) ? 'Revertir pago registrado' : 'Registrar pago'}
                                                            </DropdownItem>
                                                        )}
                                                        {invoice.importadoDelSAT !== true &&
                                                            invoice.metodoPago === 'PPD' &&
                                                            parseFloat(invoice.saldoPendiente) > 0 &&
                                                            invoice.cerradaConNotaCredito !== true && (
                                                            <>
                                                                <DropdownItem href="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault()
                                                                        _cerrarSaldoPendiente(invoice._id)
                                                                    }}>
                                                                    <i className="mdi mdi-cash-check font-size-16 text-warning mr-2"></i> Cerrar saldo pendiente
                                                                </DropdownItem>
                                                                <div className="dropdown-divider"></div>
                                                            </>
                                                        )}
                                                        {invoice.importadoDelSAT !== true && (
                                                            <DropdownItem href="#"
                                                                onClick={() => {
                                                                    setCancelInvoiceModal(true)
                                                                    setInvoiceToCancel(invoice.uuid)
                                                                    setCancelReason()
                                                                    setFolioSustitucion('')
                                                                    toggleFolioSustitucion(false)
                                                                }}>
                                                                <i className="mdi mdi-cancel font-size-16 text-danger mr-2"></i> Cancelar Factura
                                                            </DropdownItem>
                                                        )}
                                                    </>
                                                )}

                                                {(['cancelada', 'cancelación en proceso'].includes(invoice.status)) && (
                                                    <>
                                                        {invoice.status === 'cancelación en proceso' && (
                                                            <>
                                                                <DropdownItem href="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault()
                                                                        _verificarFacturaSat(invoice._id)
                                                                    }}>
                                                                    <i className="mdi mdi-refresh font-size-16 text-warning mr-2"></i> Actualizar estado en SAT
                                                                </DropdownItem>
                                                                <div className="dropdown-divider"></div>
                                                            </>
                                                        )}
                                                        <DropdownItem href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                _downloadAcuseCancelacion(invoice.uuid, 'pdf')
                                                            }}>
                                                            <i className="bx bxs-file-pdf font-size-16 text-success mr-2"></i> Obtener Acuse de Cancelación (PDF)
                                                        </DropdownItem>
                                                        <DropdownItem href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                _downloadAcuseCancelacion(invoice.uuid, 'xml')
                                                            }}>
                                                            <i className="bx bxs-file-blank font-size-16 text-success mr-2"></i> Obtener Acuse de Cancelación (XML)
                                                        </DropdownItem>
                                                    </>
                                                )}

                                                {(invoice.status === 'programada' || invoice.status === 'fallida') && (
                                                    <>
                                                        <div className="dropdown-divider"></div>
                                                        <DropdownItem href={`/facturas/nueva/${invoice._id}`}>
                                                            <i className="mdi mdi-pencil font-size-16 text-primary mr-2"></i> Editar Factura
                                                        </DropdownItem>
                                                        <DropdownItem
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                _openScheduleModal(invoice)
                                                            }}
                                                        >
                                                            <i className="mdi mdi-calendar-clock font-size-16 text-warning mr-2"></i> Gestionar Programación
                                                        </DropdownItem>
                                                    </>
                                                )}
                                            </DropdownMenu>
                                        </UncontrolledDropdown>
                                    </Col>
                                </Row>
                                {invoice.status === 'fallida' && invoice.schedule?.errorReason && (
                                    <Row className="mt-2">
                                        <Col sm={12}>
                                            <small style={{ color: '#721c24' }}>
                                                <strong>Motivo de falla:</strong> {invoice.schedule.errorReason}
                                            </small>
                                        </Col>
                                    </Row>
                                )}
                            </CardBody>
                        </Card>
                    ))}
                </CardBody>
            </Card>

            {cancelInvoiceModal && __renderCancelarFacturaModal()}

            {invoiceToSchedule && (
                <ProgramarFacturaModal
                    facturaId={invoiceToSchedule._id}
                    isOpen={Boolean(invoiceToSchedule)}
                    title="Actualizar programación"
                    submitLabel="Guardar cambios"
                    initialSchedule={getProgramacionInicial(invoiceToSchedule)}
                    onRequestClose={_closeScheduleModal}
                    onSuccess={async () => {
                        _closeScheduleModal()
                        await _getInvoices()
                    }}
                />
            )}

            {invoiceForPayment && (
                <RegistrarPagoModal
                    invoice={invoiceForPayment}
                    isOpen={Boolean(invoiceForPayment)}
                    onRequestClose={() => setInvoiceForPayment(null)}
                    onSuccess={async () => {
                        setInvoiceForPayment(null)
                        await _getInvoices()
                    }}
                />
            )}
        </React.Fragment>
    )
}

export default withRouter(Table)
